import { Component, OnInit, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { SimpleModalComponent } from "ngx-simple-modal";
import {
    AMPLIFIER_TEXT,
    AmplifierCurrentConfig,
    AmplifierConfigInfo,
    DwdmAmplifierLink,
    DwdmCircuitAmplifierModeTypeData,
    DwdmCircuitAmplifierProfileData,
    DwdmCircuitAmplifierSettingsData
} from "../amplifiers-step.model";
import { NmsInputNumberComponent } from "@nms-angular-toolkit/nms-input-number";
import { TranslationHelperService } from "@nms-ng2/app/shared/services/util/translation-helper.service";
import { TranslatedText, TranslationKey } from "@nms-ng2/app/shared/models/translation.models";
import { ConfigAmplifierService } from "./services/config-amplifier.service";
import { NgForm } from "@angular/forms";

/**
 * Modal de configuração de Amplificadores.
 */
@Component({
    selector: "config-amplifier",
    templateUrl: "./config-amplifier.component.html",
    styleUrls: ["./config-amplifier.component.scss"]
})
export class ConfigAmplifierComponent extends SimpleModalComponent<AmplifierConfigInfo, AmplifierConfigInfo>
        implements AmplifierConfigInfo, OnInit {

    private readonly amplifierValueTooltipKey: TranslationKey = "dwdm.circuit.wizard.modal.amplifier.value.tooltip";
    private readonly amplifierLimitTooltipKey: TranslationKey = "dwdm.circuit.wizard.modal.amplifier.limit.tooltip";
    private readonly amplifierLosThresholdTooltipKey: TranslationKey =
        "dwdm.circuit.wizard.modal.amplifier.los.threshold.tooltip";

    private readonly cache = new Map<string, AmplifierCurrentConfig>();

    @ViewChildren(NmsInputNumberComponent)
    nmsInputsSearch: QueryList<NmsInputNumberComponent>;

    @ViewChild("amplifierConfigForm")
    amplifierConfigForm: NgForm;

    pageTitle: string = "dwdm.circuit.wizard.modal.amplifier.configurator";
    amplificatorValue: string;
    amplificatorLimit: string;

    amplifierValueTooltipText: TranslatedText;
    amplifierLimitTooltipText: TranslatedText;
    amplifierLosThresholdTooltipText: TranslatedText;

    links: Array<DwdmAmplifierLink>;
    amplifierLinks: Array<DwdmAmplifierLink>;
    profile: DwdmCircuitAmplifierProfileData;
    mode: DwdmCircuitAmplifierModeTypeData;
    value: number;
    limit: number;
    losAction: boolean;
    losThreshold: number;

    currentAmplifier: AmplifierCurrentConfig;

    constructor(private readonly translationHelperService: TranslationHelperService,
            private readonly configAmplifiersService: ConfigAmplifierService) {
        super();
    }

    async ngOnInit() {
        this.mode = this.mode || DwdmCircuitAmplifierModeTypeData.GAIN;
        await this.updateAmplifier(this.profile, this.mode, this.buildInitialAmplifier.bind(this));
        this.updateAmplifierTooltipRange(this.profile, this.mode);
        this.updateAmplifierLabel(this.mode);
    }

    async onProfileChange(profile: DwdmCircuitAmplifierProfileData) {
        this.onChangeUpdate(profile, this.mode);
    }

    async onModeChange(mode: DwdmCircuitAmplifierModeTypeData) {
        this.updateAmplifierLabel(mode);
        this.onChangeUpdate(this.profile, mode);
    }

    async saveAmplifier() {
        this.result = {
            profile: this.profile,
            links: this.links,
            mode: this.mode,
            value: this.currentAmplifier.value,
            limit: this.currentAmplifier.limit,
            losThreshold: this.currentAmplifier.losThreshold,
            losAction: this.currentAmplifier.losAction
        };

        this.close();
    }

    isDisabled() {
        return this.amplifierConfigForm?.invalid;
    }

    private async updateAmplifier(profile: DwdmCircuitAmplifierProfileData, mode: DwdmCircuitAmplifierModeTypeData,
            buildCurrentAmplifier: (amplifierSettings: DwdmCircuitAmplifierSettingsData) => AmplifierCurrentConfig) {
        let amplifierCurrentConfig: AmplifierCurrentConfig;
        const key: string = `${profile}-${mode}`;

        if (!this.cache.get(key)) {
            const amplifierSettings = await this.configAmplifiersService.getAmplifierSettings(profile, mode).toPromise();
            amplifierCurrentConfig = buildCurrentAmplifier(amplifierSettings);
            this.cache.set(key, amplifierCurrentConfig);
        } else {
            amplifierCurrentConfig = this.cache.get(key);
        }

        this.currentAmplifier = amplifierCurrentConfig;
    }

    private buildCurrentAmplifierWithDefault(amplifierSettings: DwdmCircuitAmplifierSettingsData): AmplifierCurrentConfig {
        const currentValue = {
            value: amplifierSettings.defaultValue,
            limit: amplifierSettings.defaultLimit,
            losThreshold: amplifierSettings.defaultLosThreshold,
            losAction: amplifierSettings.defaultLosAction
        };

        return { ...currentValue, ...amplifierSettings };
    }

    private buildInitialAmplifier(amplifierSettings: DwdmCircuitAmplifierSettingsData): AmplifierCurrentConfig {
        const currentValue = {
            value: this.value ?? amplifierSettings.defaultValue,
            limit: this.limit ?? amplifierSettings.defaultLimit,
            losThreshold: this.losThreshold ?? amplifierSettings.defaultLosThreshold,
            losAction: this.losAction ?? amplifierSettings.defaultLosAction
        };

        return { ...currentValue, ...amplifierSettings };
    }

    private async onChangeUpdate(profile: DwdmCircuitAmplifierProfileData, mode: DwdmCircuitAmplifierModeTypeData) {
        await this.updateAmplifier(profile, mode, this.buildCurrentAmplifierWithDefault.bind(this));
        setTimeout(() => {
            this.nmsInputsSearch.forEach((comp) => comp.updateValidators());
        }, 1);
        this.updateAmplifierTooltipRange(profile, mode);
    }

    private updateAmplifierLabel(mode: DwdmCircuitAmplifierModeTypeData): void {
        const { valueText, limitText } = AMPLIFIER_TEXT[mode];

        this.amplificatorValue = valueText;
        this.amplificatorLimit = limitText;
    }

    private updateAmplifierTooltipRange(profile: DwdmCircuitAmplifierProfileData, mode: DwdmCircuitAmplifierModeTypeData): void {
        const defaultAmplifier = this.cache.get(`${profile}-${mode}`);

        this.amplifierValueTooltipText = this.translationHelperService.translateWithReplacement(
            this.amplifierValueTooltipKey, defaultAmplifier.minValue.toFixed(1), defaultAmplifier.maxValue.toFixed(1)
        );
        this.amplifierLimitTooltipText = this.translationHelperService.translateWithReplacement(
            this.amplifierLimitTooltipKey, defaultAmplifier.minLimit.toFixed(1),
            defaultAmplifier.maxLimit.toFixed(1)
        );
        this.amplifierLosThresholdTooltipText = this.translationHelperService.translateWithReplacement(
            this.amplifierLosThresholdTooltipKey, defaultAmplifier.minLosThreshold.toFixed(1),
            defaultAmplifier.maxLosThreshold.toFixed(1)
        );
    }
}
